import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';

// Component & Asset Imports
import Toptext from '../../components/Toptext';
import Globe from '../../assets/icons/globe.svg';
import Button1 from '../../components/ui/Button1';
import Button2 from '../../components/ui/Button2';
import DynamicHeading from '../../components/ui/CustomHeading';
import { FaMapMarkerAlt, FaPhone, FaEnvelope, FaBuilding, FaGlobe } from 'react-icons/fa';

gsap.registerPlugin(ScrollTrigger);

const ContactPage = () => {
    const mainRef = useRef(null);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        department: '',
        message: ''
    });

    const handleInputChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        console.log('Form submitted:', formData);
    };

    const departments = [
        'General Inquiry',
        'Sales & Business Development',
        'Technical Support',
        'Investor Relations',
        'Media & Press',
        'Partnership Opportunities',
        'Career Opportunities'
    ];

    useEffect(() => {
        document.title = 'Contact | JBS Capacitors';
        window.scrollTo(0, 0);
    }, []);

    useGSAP(() => {
        // Hero Section Intro
        const tl = gsap.timeline({ defaults: { ease: 'power3.out', duration: 0.8 } });
        tl.from('.anim-toptext', { y: -30, opacity: 0 })
          .from('.anim-main-heading span', { y: 40, opacity: 0, stagger: 0.1 }, "-=0.5")
          .from('.anim-subtext', { y: 20, opacity: 0 }, "-=0.6");

        // Animate sections on scroll
        const animateOnScroll = (selector) => {
            gsap.from(selector, {
                y: 50,
                opacity: 0,
                duration: 1,
                ease: 'power3.out',
                scrollTrigger: {
                    trigger: selector,
                    start: 'top 85%',
                    toggleActions: 'play none none none'
                }
            });
        };

        ['.anim-contact-info', '.anim-contact-form', '.anim-quick-contact'].forEach(sel => {
            const el = document.querySelector(sel);
            if (el) animateOnScroll(el);
        });
    }, { scope: mainRef });

    return (
        <div ref={mainRef} className='mt-32 flex flex-col items-center justify-center overflow-x-hidden'>
            {/* Top Heading */}
            <div className="anim-toptext">
                <Toptext icon={Globe}>
                    Get in Touch — We'd Love to Hear from You
                </Toptext>
            </div>

            {/* Main Section */}
            <div className='mt-12 text-center px-4'>
                <div className="anim-main-heading">
                    <DynamicHeading
                        parts={[
                            { text: 'Let\'s Start a' },
                            { text: 'Conversation', highlight: true },
                            { lineBreak: true },
                            { text: 'Together' },
                        ]}
                    />
                </div>
                <p className='anim-subtext font-light text-jbsGray mt-4 text-lg md:text-xl max-w-4xl mx-auto'>
                    Whether you're interested in our products, want to partner with us, or just have a question,
                    we're here to help. Reach out and let's explore how we can work together.
                </p>
            </div>

            {/* Contact Information & Map Section */}
            <div className='mt-16 w-full max-w-7xl mx-auto px-4 anim-contact-info'>
                <div className='grid grid-cols-1 lg:grid-cols-2 gap-12'>
                    {/* Contact Information */}
                    <div className='space-y-8'>
                        <motion.div
                            initial={{ opacity: 0, y: 50 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            viewport={{ once: true }}
                            className='bg-white p-8 rounded-[38px] shadow-md'
                        >
                            <h3 className='text-2xl font-bold text-jbsGray mb-6'>India Headquarters</h3>
                            <div className='space-y-6'>
                                <div className='flex items-start space-x-4'>
                                    <div className='w-12 h-12 bg-jbsBlue rounded-full flex items-center justify-center flex-shrink-0'>
                                        <FaMapMarkerAlt className='text-white text-xl' />
                                    </div>
                                    <div>
                                        <h4 className='font-semibold text-jbsGray mb-1'>Address</h4>
                                        <p className='text-gray-600 leading-relaxed'>
                                            JBS Capacitors Pvt Ltd<br />
                                            Plot No. 123, Industrial Estate<br />
                                            Bhubaneswar, Odisha 751010<br />
                                            India
                                        </p>
                                    </div>
                                </div>
                                <div className='flex items-start space-x-4'>
                                    <div className='w-12 h-12 bg-jbsBlue rounded-full flex items-center justify-center flex-shrink-0'>
                                        <FaPhone className='text-white text-xl' />
                                    </div>
                                    <div>
                                        <h4 className='font-semibold text-jbsGray mb-1'>Phone</h4>
                                        <p className='text-gray-600'>+91 674 123 4567</p>
                                        <p className='text-gray-600'>+91 674 123 4568</p>
                                    </div>
                                </div>
                                <div className='flex items-start space-x-4'>
                                    <div className='w-12 h-12 bg-jbsBlue rounded-full flex items-center justify-center flex-shrink-0'>
                                        <FaEnvelope className='text-white text-xl' />
                                    </div>
                                    <div>
                                        <h4 className='font-semibold text-jbsGray mb-1'>Email</h4>
                                        <p className='text-gray-600'>info@jbscapacitors.com</p>
                                        <p className='text-gray-600'>sales@jbscapacitors.com</p>
                                    </div>
                                </div>
                                <div className='flex items-start space-x-4'>
                                    <div className='w-12 h-12 bg-jbsBlue rounded-full flex items-center justify-center flex-shrink-0'>
                                        <FaBuilding className='text-white text-xl' />
                                    </div>
                                    <div>
                                        <h4 className='font-semibold text-jbsGray mb-1'>Business Hours</h4>
                                        <p className='text-gray-600'>Monday - Friday: 9:00 AM - 6:00 PM</p>
                                        <p className='text-gray-600'>Saturday: 9:00 AM - 1:00 PM</p>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                        {/* UK Office Info */}
                        <motion.div
                            initial={{ opacity: 0, y: 50 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                            viewport={{ once: true }}
                            className='bg-gray-50 p-8 rounded-[38px]'
                        >
                            <h3 className='text-xl font-bold text-jbsGray mb-4'>UK Operations</h3>
                            <div className='flex items-start space-x-4'>
                                <div className='w-10 h-10 bg-jbsBlue rounded-full flex items-center justify-center flex-shrink-0'>
                                    <FaGlobe className='text-white' />
                                </div>
                                <div>
                                    <p className='text-gray-600'>
                                        <strong>JBS Robotics & Applied AI Research (UK)</strong><br />
                                        London, United Kingdom<br />
                                        <a href="mailto:uk@jbsrobotics.com" className='text-jbsBlue hover:underline'>
                                            uk@jbsrobotics.com
                                        </a>
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                    {/* Map Embed */}
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.4 }}
                        viewport={{ once: true }}
                        className='bg-white p-6 rounded-[38px] shadow-md anim-map'
                    >
                        <h3 className='text-xl font-bold text-jbsGray mb-4'>Our Location</h3>
                        <div className='w-full h-[650px] rounded-2xl overflow-hidden'>
                            <iframe
                                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3742.1234567890123!2d85.8245!3d20.2961!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjDCsDE3JzQ2LjAiTiA4NcKwNDknMjguMiJF!5e0!3m2!1sen!2sin!4v1234567890123"
                                width="100%"
                                height="100%"
                                style={{ border: 0 }}
                                allowFullScreen=""
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                title="JBS Capacitors Location"
                            ></iframe>
                        </div>
                    </motion.div>
                </div>
            </div>

            {/* Contact Form Section */}
            <div className='mt-16 w-full max-w-4xl mx-auto px-4 anim-contact-form'>
                <div className='text-center mb-8'>
                    <DynamicHeading
                        parts={[
                            { text: 'Send Us a' },
                            { text: 'Message', highlight: true },
                        ]}
                    />
                    <p className='text-gray-600 mt-4'>
                        Fill out the form below and we'll get back to you within 24 hours.
                    </p>
                </div>
                <div className='bg-white p-8 rounded-[38px] shadow-md'>
                    <form onSubmit={handleSubmit} className='space-y-6'>
                        <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                            <div>
                                <label htmlFor="name" className='block text-sm font-semibold text-jbsGray mb-2'>
                                    Full Name *
                                </label>
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    required
                                    className='w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-jbsBlue focus:border-transparent transition-all duration-200'
                                    placeholder="Enter your full name"
                                />
                            </div>
                            <div>
                                <label htmlFor="email" className='block text-sm font-semibold text-jbsGray mb-2'>
                                    Email Address *
                                </label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    required
                                    className='w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-jbsBlue focus:border-transparent transition-all duration-200'
                                    placeholder="Enter your email address"
                                />
                            </div>
                        </div>
                        <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                            <div>
                                <label htmlFor="subject" className='block text-sm font-semibold text-jbsGray mb-2'>
                                    Subject *
                                </label>
                                <input
                                    type="text"
                                    id="subject"
                                    name="subject"
                                    value={formData.subject}
                                    onChange={handleInputChange}
                                    required
                                    className='w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-jbsBlue focus:border-transparent transition-all duration-200'
                                    placeholder="What's this about?"
                                />
                            </div>
                            <div>
                                <label htmlFor="department" className='block text-sm font-semibold text-jbsGray mb-2'>
                                    Department
                                </label>
                                <select
                                    id="department"
                                    name="department"
                                    value={formData.department}
                                    onChange={handleInputChange}
                                    className='w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-jbsBlue focus:border-transparent transition-all duration-200'
                                >
                                    <option value="">Select a department</option>
                                    {departments.map((dept, index) => (
                                        <option key={index} value={dept}>{dept}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div>
                            <label htmlFor="message" className='block text-sm font-semibold text-jbsGray mb-2'>
                                Message *
                            </label>
                            <textarea
                                id="message"
                                name="message"
                                value={formData.message}
                                onChange={handleInputChange}
                                required
                                rows="6"
                                className='w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-jbsBlue focus:border-transparent transition-all duration-200 resize-none'
                                placeholder="Tell us how we can help you..."
                            ></textarea>
                        </div>
                        <div className='flex justify-center pt-4'>
                            <Button1 width={"200px"} type="submit">
                                Send Message
                            </Button1>
                        </div>
                    </form>
                </div>
            </div>

            {/* Quick Contact CTA */}
            <div className='mt-16 text-center px-4 mb-20 anim-quick-contact'>
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5 }}
                    viewport={{ once: true }}
                    className='bg-gradient-to-r from-jbsBlue to-blue-600 p-8 rounded-[38px] text-white'
                >
                    <h3 className='text-2xl font-bold mb-4'>Need Immediate Assistance?</h3>
                    <p className='text-lg mb-6 opacity-90'>
                        For urgent inquiries, call us directly or send us a WhatsApp message.
                    </p>
                    <div className='flex flex-col sm:flex-row gap-4 justify-center'>
                        <Button2 width={"200px"}>
                            <a href="tel:+916741234567" className='text-white'>
                                Call Now
                            </a>
                        </Button2>
                        <Button2 width={"200px"}>
                            <a href="https://wa.me/916741234567" target="_blank" rel="noopener noreferrer" className='text-white'>
                                WhatsApp
                            </a>
                        </Button2>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default ContactPage;
